import { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { API, formatWeight, formatDateTime, displayDate } from '@/lib/api';
import { useSubmitGuard } from '@/lib/useSubmitGuard';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { DecimalInput } from '@/components/ui/decimal-input';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import { ArrowLeft, Plus, Minus, Pencil, Trash2, Lock, Calendar, ChevronDown, ChevronUp, Filter, X, Undo2 } from 'lucide-react';

const REVERSIBLE_OPS = ['sale', 'purchase', 'sale_return', 'purchase_return', 'poly_add', 'poly_subtract', 'gold_add', 'gold_subtract'];

const sanitizeDecimal = (v) => { let c = v.replace(/[^0-9.]/g, ''); const p = c.split('.'); if (p.length > 2) c = p[0] + '.' + p.slice(1).join(''); return c; };

export default function BoxDetailPage() {
  const { boxId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [box, setBox] = useState(null);
  const [history, setHistory] = useState([]);
  const [adjustType, setAdjustType] = useState('gold');
  const [adjustAmount, setAdjustAmount] = useState('');
  const [adjustNotes, setAdjustNotes] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [opTypeFilter, setOpTypeFilter] = useState('');
  const [showEdit, setShowEdit] = useState(false);
  const [allItems, setAllItems] = useState([]);
  const [editForm, setEditForm] = useState({});
  const [collapsedDates, setCollapsedDates] = useState({});
  const canAdjust = ['admin', 'store_manager', 'analyst'].includes(user?.role);
  const canEdit = ['admin', 'store_manager', 'analyst'].includes(user?.role);
  const isAdmin = ['admin', 'analyst'].includes(user?.role);
  const [editingDateId, setEditingDateId] = useState(null);
  const [editDateValue, setEditDateValue] = useState('');
  const { busy, run } = useSubmitGuard('Please wait — the previous action is still processing');

  const fetchBox = async () => {
    try {
      const res = await API.get(`/boxes/${boxId}`);
      setBox(res.data);
    } catch(e) { toast.error('Failed to load box'); navigate('/boxes'); }
  };

  const fetchHistory = async () => {
    try {
      const params = {};
      if (startDate) params.start_date = startDate;
      if (endDate) params.end_date = endDate;
      if (opTypeFilter) params.op_type = opTypeFilter;
      const res = await API.get(`/boxes/${boxId}/history`, { params });
      setHistory(res.data);
    } catch(e) { console.error(e); }
  };

  const fetchItems = async () => { try { setAllItems((await API.get('/items')).data); } catch(e) {} };

  useEffect(() => { fetchBox(); fetchItems(); }, [boxId]);
  useEffect(() => { fetchHistory(); }, [boxId, startDate, endDate, opTypeFilter]);

  // Group by date with summary stats
  const groupedHistory = useMemo(() => {
    const groups = {};
    history.forEach(ev => {
      const d = ev.date || (ev.created_at ? ev.created_at.split('T')[0] : 'Unknown');
      if (!groups[d]) groups[d] = { events: [], totalGoldMg: 0, totalPolyMg: 0, saleCount: 0, purchaseCount: 0 };
      groups[d].events.push(ev);
      const gw = ev.gold_weight_g || 0;
      const pw = ev.polyethene_weight_g || 0;
      const ot = ev.operation_type || '';
      if (ot.includes('sale') && !ot.includes('return')) { groups[d].totalGoldMg += gw; groups[d].saleCount++; }
      else if (ot.includes('purchase') && !ot.includes('return')) { groups[d].totalGoldMg += gw; groups[d].purchaseCount++; }
      if (ot.includes('poly')) groups[d].totalPolyMg += pw;
    });
    return Object.entries(groups)
      .sort(([a], [b]) => b.localeCompare(a))
      .map(([date, data]) => ({ date, ...data }));
  }, [history]);

  // All unique operation types for filter
  const opTypes = useMemo(() => {
    const types = new Set();
    history.forEach(ev => { if (ev.operation_type) types.add(ev.operation_type); });
    return Array.from(types).sort();
  }, [history]);

  const toggleDateCollapse = (date) => {
    setCollapsedDates(prev => ({ ...prev, [date]: !prev[date] }));
  };

  const clearFilters = () => { setStartDate(''); setEndDate(''); setOpTypeFilter(''); };
  const hasFilters = startDate || endDate || opTypeFilter;

  const editAutoGold = useMemo(() => {
    const g = parseFloat(editForm.gross_weight_g) || 0;
    const p = parseFloat(editForm.polyethene_weight_g) || 0;
    const b = parseFloat(editForm.box_weight_g) || 0;
    return (g - p - b).toFixed(3);
  }, [editForm.gross_weight_g, editForm.polyethene_weight_g, editForm.box_weight_g]);

  const openEditDialog = () => {
    if (!box) return;
    setEditForm({
      box_type: box.box_type, gold_purity: box.gold_purity,
      description: box.description || '',
      gross_weight_g: String(box.gross_weight_g || 0),
      polyethene_weight_g: String(box.polyethene_weight_g || 0),
      box_weight_g: String(box.box_weight_g || 0),
      item_names: box.item_names || [],
    });
    setShowEdit(true);
  };

  const handleEditSave = async () => {
    return run(async () => {
    try {
      const res = await API.put(`/boxes/${boxId}`, {
        box_type: editForm.box_type, description: editForm.description,
        gross_weight_g: parseFloat(editForm.gross_weight_g) || 0,
        polyethene_weight_g: parseFloat(editForm.polyethene_weight_g) || 0,
        box_weight_g: parseFloat(editForm.box_weight_g) || 0,
        item_names: editForm.item_names,
        updated_by: user?.role || '',
      });
      if (res.data?.status === 'warning' && res.data?.warning === 'negative_gold') {
        const proceed = window.confirm(
          `Warning: Gold weight would be negative (${res.data.computed_gold_g}g).\n\n` +
          `To maintain data coherence, gold will be set to 0g.\n\n` +
          `Gross: ${res.data.gross_g}g | Poly: ${res.data.poly_g}g | Box Wt: ${res.data.box_weight_g}g\n` +
          `Computed Gold: ${res.data.computed_gold_g}g (will be clamped to 0g)\n\n` +
          `Proceed anyway?`
        );
        if (!proceed) return;
        await API.put(`/boxes/${boxId}`, {
          box_type: editForm.box_type, description: editForm.description,
          gross_weight_g: parseFloat(editForm.gross_weight_g) || 0,
          polyethene_weight_g: parseFloat(editForm.polyethene_weight_g) || 0,
          box_weight_g: parseFloat(editForm.box_weight_g) || 0,
          item_names: editForm.item_names,
          updated_by: user?.role || '',
          force: true,
        });
      }
      toast.success('Box updated'); setShowEdit(false); fetchBox(); fetchHistory();
    } catch(err) { toast.error(err.response?.data?.detail || 'Failed'); }
    });
  };

  const handleDeleteBox = async () => {
    if (!window.confirm(`Delete box "${boxId}"?`)) return;
    return run(async () => {
      try { await API.delete(`/boxes/${boxId}`); toast.success('Box deleted'); navigate('/boxes'); }
      catch(err) { toast.error(err.response?.data?.detail || 'Failed'); }
    });
  };

  const handleAdjust = async (positive) => {
    const amount = parseFloat(adjustAmount);
    if (!amount || amount <= 0) { toast.error('Enter valid weight'); return; }
    return run(async () => {
      try {
        await API.post(`/boxes/${boxId}/adjust-weight`, { weight_type: adjustType, adjustment_g: positive ? amount : -amount, submitted_by: user?.role, notes: adjustNotes });
        toast.success(`${adjustType} ${positive ? 'added' : 'subtracted'}`); setAdjustAmount(''); setAdjustNotes(''); fetchBox(); fetchHistory();
      } catch(err) { toast.error(err.response?.data?.detail || 'Failed'); }
    });
  };

  const handleReverse = async (ev) => {
    if (!window.confirm(`Reverse this ${ev.operation_type.replace(/_/g, ' ')} operation?\n\nGold: ${formatWeight(ev.gold_weight_g)}\nPoly: ${formatWeight(ev.polyethene_weight_g)}\n\nA compensating reverse entry will be created.`)) return;
    return run(async () => {
      try {
        await API.post(`/operations/${ev.id}/reverse`, { notes: '' });
        toast.success('Operation reversed successfully'); fetchBox(); fetchHistory();
      } catch(err) { toast.error(err.response?.data?.detail || 'Reverse failed'); }
    });
  };

  const handleDateChange = async (ev) => {
    if (!editDateValue || editDateValue === ev.date) { setEditingDateId(null); return; }
    return run(async () => {
      try {
        await API.patch(`/operation-events/${ev.id}/date`, { new_date: editDateValue });
        toast.success(`Date changed to ${editDateValue.split('-').reverse().join('-')}`);
        setEditingDateId(null);
        fetchBox(); fetchHistory();
      } catch(err) { toast.error(err.response?.data?.detail || 'Failed to change date'); setEditingDateId(null); }
    });
  };

  const opBadgeVariant = (type) => {
    if (type.includes('sale') && !type.includes('return')) return 'destructive';
    if (type.includes('purchase') && !type.includes('return')) return 'default';
    if (type.includes('merge') || type.includes('split')) return 'secondary';
    if (type === 'admin_edit') return 'outline';
    return 'outline';
  };

  const opColor = (type) => {
    if (type.includes('sale') && !type.includes('return')) return 'border-l-rose-500';
    if (type === 'sale_return') return 'border-l-pink-400';
    if (type.includes('purchase') && !type.includes('return')) return 'border-l-blue-500';
    if (type === 'purchase_return') return 'border-l-sky-400';
    if (type.includes('merge')) return 'border-l-orange-500';
    if (type.includes('split')) return 'border-l-amber-500';
    if (type.includes('gold_add')) return 'border-l-emerald-500';
    if (type.includes('gold_subtract')) return 'border-l-red-500';
    if (type.includes('poly')) return 'border-l-violet-500';
    if (type === 'admin_edit') return 'border-l-yellow-500';
    return 'border-l-muted-foreground';
  };

  const formatDateLabel = (d) => {
    if (!d) return '—';
    const m = String(d).match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (m) {
      try { 
        const dt = new Date(d + 'T00:00:00');
        const weekday = dt.toLocaleDateString('en-IN', { weekday: 'short' });
        return `${m[3]}-${m[2]}-${m[1]} (${weekday})`; 
      } catch { return `${m[3]}-${m[2]}-${m[1]}`; }
    }
    return d;
  };

  if (!box) return <div className="p-4">Loading...</div>;

  return (
    <div data-testid="box-detail-page">
      <div className="flex items-center gap-3 mb-4 md:mb-6">
        <Button variant="ghost" size="icon" onClick={() => navigate('/boxes')} data-testid="box-detail-back"><ArrowLeft size={20} /></Button>
        <div className="min-w-0 flex-1">
          <h1 className="text-lg sm:text-2xl font-semibold truncate" style={{ fontFamily: 'var(--font-heading)' }}>Box <span className="font-mono">{box.box_id}</span></h1>
          <div className="flex items-center gap-1 sm:gap-2 mt-1 flex-wrap">
            <Badge variant={box.box_type === 'brand' ? 'default' : 'secondary'} className="text-xs">{box.box_type}</Badge>
            <span className={`status-badge purity-${box.gold_purity} text-xs`}>{box.gold_purity}</span>
            {box.is_active ? <span className="status-badge status-approved text-xs">Active</span> : <span className="status-badge status-rejected text-xs">Inactive</span>}
          </div>
          {box.description && <p className="text-xs text-muted-foreground mt-1">{box.description}</p>}
        </div>
        {canEdit && (
          <div className="flex gap-1 shrink-0">
            <Button variant="outline" size="sm" className="h-9 px-3" onClick={openEditDialog} data-testid="box-detail-edit-btn"><Pencil size={14} className="mr-1" />Edit</Button>
            <Button variant="outline" size="sm" className="h-9 px-3 text-destructive border-destructive/30 hover:bg-destructive/10" onClick={handleDeleteBox} data-testid="box-detail-delete-btn"><Trash2 size={14} /></Button>
          </div>
        )}
      </div>

      <Tabs defaultValue="details">
        <TabsList className="w-full sm:w-auto grid grid-cols-3 sm:flex">
          <TabsTrigger value="details" className="text-xs sm:text-sm">Details</TabsTrigger>
          <TabsTrigger value="history" className="text-xs sm:text-sm" data-testid="history-tab">History ({history.length})</TabsTrigger>
          {canAdjust && <TabsTrigger value="adjust" className="text-xs sm:text-sm">Adjust</TabsTrigger>}
        </TabsList>

        <TabsContent value="details" className="mt-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Card><CardContent className="pt-4 px-3"><p className="text-xs text-muted-foreground">Gross Weight</p><p className="text-lg font-mono font-semibold">{formatWeight(box.gross_weight_g)}</p></CardContent></Card>
            <Card><CardContent className="pt-4 px-3"><p className="text-xs text-muted-foreground">Net Gold</p><p className="text-lg font-mono font-semibold" style={{ color: 'hsl(43, 96%, 56%)' }}>{formatWeight(box.gold_weight_g)}</p></CardContent></Card>
            <Card><CardContent className="pt-4 px-3"><p className="text-xs text-muted-foreground">Polythene</p><p className="text-lg font-mono font-semibold">{formatWeight(box.polyethene_weight_g)}</p></CardContent></Card>
            <Card><CardContent className="pt-4 px-3"><p className="text-xs text-muted-foreground">Box Weight</p><p className="text-lg font-mono font-semibold">{formatWeight(box.box_weight_g)}</p></CardContent></Card>
          </div>
          <Card className="mt-3">
            <CardHeader className="pb-2"><CardTitle className="text-sm">Items</CardTitle></CardHeader>
            <CardContent>
              {(box.item_names || []).length > 0 ? (
                <div className="flex flex-wrap gap-1.5">{box.item_names.map((n, i) => <Badge key={i} variant="outline" className="text-xs">{n}</Badge>)}</div>
              ) : <p className="text-xs text-muted-foreground">No items assigned</p>}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="history" className="mt-4">
          {/* Date Range Filter */}
          <Card className="mb-4">
            <CardContent className="pt-3 pb-3 px-3">
              <div className="flex items-center gap-1.5 mb-2">
                <Filter size={14} className="text-muted-foreground" />
                <span className="text-xs font-semibold text-muted-foreground">Filter History</span>
                {hasFilters && (
                  <Button variant="ghost" size="sm" className="h-6 px-2 text-xs ml-auto" onClick={clearFilters} data-testid="history-clear-filters">
                    <X size={12} className="mr-1" />Clear
                  </Button>
                )}
              </div>
              <div className="flex flex-col sm:flex-row gap-2">
                <div className="flex items-center gap-2 flex-1">
                  <div className="flex-1">
                    <label className="text-[10px] text-muted-foreground mb-0.5 block">From</label>
                    <Input type="date" value={startDate} onChange={e => setStartDate(e.target.value)} className="h-8 text-xs" data-testid="history-start-date" />
                  </div>
                  <span className="text-muted-foreground text-xs mt-4">to</span>
                  <div className="flex-1">
                    <label className="text-[10px] text-muted-foreground mb-0.5 block">To</label>
                    <Input type="date" value={endDate} onChange={e => setEndDate(e.target.value)} className="h-8 text-xs" data-testid="history-end-date" />
                  </div>
                </div>
                <div className="sm:w-40">
                  <label className="text-[10px] text-muted-foreground mb-0.5 block">Type</label>
                  <Select value={opTypeFilter || 'all'} onValueChange={v => setOpTypeFilter(v === 'all' ? '' : v)}>
                    <SelectTrigger className="h-8 text-xs" data-testid="history-type-filter"><SelectValue placeholder="All types" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All types</SelectItem>
                      {opTypes.map(t => <SelectItem key={t} value={t}>{t.replace(/_/g, ' ')}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Date-wise Modules */}
          {groupedHistory.length > 0 ? groupedHistory.map(group => (
            <Card key={group.date} className="mb-3" data-testid={`history-date-group-${group.date}`}>
              {/* Date Header with Summary */}
              <CardHeader
                className="pb-0 pt-3 px-3 sm:px-4 cursor-pointer hover:bg-muted/30 rounded-t-lg transition-colors"
                onClick={() => toggleDateCollapse(group.date)}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Calendar size={14} className="text-muted-foreground" />
                    <span className="text-sm font-semibold">{formatDateLabel(group.date)}</span>
                    <span className="text-xs text-muted-foreground">({group.events.length} events)</span>
                  </div>
                  <div className="flex items-center gap-3">
                    {/* Quick Stats */}
                    <div className="hidden sm:flex items-center gap-2 text-[10px]">
                      {group.saleCount > 0 && <span className="px-1.5 py-0.5 rounded bg-rose-950/40 text-rose-400">{group.saleCount} sales</span>}
                      {group.purchaseCount > 0 && <span className="px-1.5 py-0.5 rounded bg-blue-950/40 text-blue-400">{group.purchaseCount} purchases</span>}
                    </div>
                    {collapsedDates[group.date] ? <ChevronDown size={16} className="text-muted-foreground" /> : <ChevronUp size={16} className="text-muted-foreground" />}
                  </div>
                </div>
              </CardHeader>

              {/* Events List */}
              {!collapsedDates[group.date] && (
                <CardContent className="p-0 pt-2">
                  <div className="divide-y divide-border/40">
                    {group.events.map(ev => (
                      <div key={ev.id} className={`px-3 sm:px-4 py-2.5 border-l-2 ${opColor(ev.operation_type)} hover:bg-muted/20 transition-colors`} data-testid={`history-event-${ev.id}`}>
                        <div className="flex items-center justify-between mb-1">
                          <Badge variant={opBadgeVariant(ev.operation_type)} className="text-[10px] sm:text-xs">
                            {ev.operation_type.replace(/_/g, ' ')}
                          </Badge>
                          <span className="text-[10px] sm:text-xs text-muted-foreground">
                            {ev.created_at ? new Date(ev.created_at).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', second: '2-digit', timeZone: 'Asia/Kolkata' }) : ''}
                          </span>
                        </div>
                        {/* Before/After Weight */}
                        {(ev.before_gross_g != null || ev.after_gross_g != null) && (
                          <div className="flex items-center gap-1.5 text-xs font-mono mb-1">
                            <span className="text-muted-foreground/70 w-12 text-right shrink-0">Before:</span>
                            <span className="font-semibold">{formatWeight(ev.before_gross_g)}</span>
                            <span className="text-muted-foreground/50 px-0.5">&rarr;</span>
                            <span className="text-muted-foreground/70 w-10 text-right shrink-0">After:</span>
                            <span className="font-semibold">{formatWeight(ev.after_gross_g)}</span>
                            {ev.before_gross_g != null && ev.after_gross_g != null && ev.before_gross_g !== ev.after_gross_g && (
                              <span className={`ml-1 font-semibold ${ev.after_gross_g > ev.before_gross_g ? 'text-emerald-400' : 'text-rose-400'}`}>
                                ({ev.after_gross_g > ev.before_gross_g ? '+' : ''}{(ev.after_gross_g - ev.before_gross_g).toFixed(3)}g)
                              </span>
                            )}
                          </div>
                        )}
                        <div className="flex gap-3 sm:gap-4 text-xs">
                          {ev.gold_weight_g > 0 && <span>Gold: <b className="weight-value">{formatWeight(ev.gold_weight_g)}</b></span>}
                          {ev.polyethene_weight_g > 0 && <span>Poly: <b className="weight-value">{formatWeight(ev.polyethene_weight_g)}</b></span>}
                          {ev.gold_purity && <span className={`status-badge purity-${ev.gold_purity} text-[10px]`}>{ev.gold_purity}</span>}
                        </div>
                        {(ev.customer_name || ev.supplier_name || ev.salesman_name) && (
                          <div className="flex gap-2 mt-1 text-[10px] sm:text-xs text-muted-foreground">
                            {ev.customer_name && <span>Customer: {ev.customer_name}</span>}
                            {ev.supplier_name && <span>Supplier: {ev.supplier_name}</span>}
                            {ev.salesman_name && <span>Salesman: {ev.salesman_name}</span>}
                          </div>
                        )}
                        {ev.notes && <p className="text-[10px] sm:text-xs text-muted-foreground mt-1 italic">{ev.notes}</p>}
                        {ev.date_changed_by && (
                          <p className="text-[9px] text-amber-500/70 mt-0.5 flex items-center gap-1">
                            <Calendar size={9} />Date changed{ev.original_date ? ` from ${ev.original_date.split('-').reverse().join('-')}` : ''} by {ev.date_changed_by}
                          </p>
                        )}
                        <div className="flex items-center justify-between mt-1">
                          <div className="flex items-center gap-2">
                            {ev.submitted_by && <span className="text-[10px] text-muted-foreground">by {ev.submitted_by}</span>}
                            {isAdmin && (
                              editingDateId === ev.id ? (
                                <span className="flex items-center gap-1">
                                  <Input type="date" value={editDateValue} onChange={e => setEditDateValue(e.target.value)} className="h-5 text-[10px] w-32 px-1" data-testid={`event-date-input-${ev.id}`} onKeyDown={e => { if (e.key === 'Enter') handleDateChange(ev); if (e.key === 'Escape') setEditingDateId(null); }} autoFocus />
                                  <Button variant="ghost" size="sm" className="h-5 px-1 text-[10px] text-emerald-400" onClick={() => handleDateChange(ev)} data-testid={`event-date-save-${ev.id}`}>Save</Button>
                                  <Button variant="ghost" size="sm" className="h-5 px-1 text-[10px] text-muted-foreground" onClick={() => setEditingDateId(null)}>Cancel</Button>
                                </span>
                              ) : (
                                <Button variant="ghost" size="sm" className="h-5 px-1.5 text-[10px] text-muted-foreground hover:text-amber-400" onClick={() => { setEditingDateId(ev.id); setEditDateValue(ev.date || ''); }} data-testid={`event-edit-date-${ev.id}`}>
                                  <Calendar size={10} className="mr-0.5" />Edit Date
                                </Button>
                              )
                            )}
                          </div>
                          <div className="flex items-center gap-1">
                            {canEdit && REVERSIBLE_OPS.includes(ev.operation_type) && !ev.reversed_by && (
                              <Button variant="ghost" size="sm" disabled={busy} className="h-5 px-1.5 text-[10px] text-muted-foreground hover:text-amber-400" onClick={() => handleReverse(ev)} data-testid={`reverse-op-${ev.id}`}>
                                <Undo2 size={10} className="mr-0.5" />Reverse
                              </Button>
                            )}
                            {ev.reversed_by && <Badge variant="outline" className="text-[9px] px-1 py-0 text-muted-foreground border-muted-foreground/30">Reversed</Badge>}
                            {ev.is_reversal && <Badge variant="outline" className="text-[9px] px-1 py-0 text-amber-500/70 border-amber-500/30">Reversal</Badge>}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              )}
            </Card>
          )) : (
            <Card>
              <CardContent className="py-12 text-center text-muted-foreground text-sm">
                No history{hasFilters ? ' for the selected filters' : ' yet'}
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {canAdjust && (
          <TabsContent value="adjust" className="mt-4">
            <Card className="max-w-md"><CardHeader className="pb-2"><CardTitle className="text-base">Adjust Weight</CardTitle></CardHeader><CardContent className="space-y-3">
              <div className="flex gap-2"><Button variant={adjustType === 'gold' ? 'default' : 'outline'} size="sm" onClick={() => setAdjustType('gold')}>Gold</Button><Button variant={adjustType === 'polyethene' ? 'default' : 'outline'} size="sm" onClick={() => setAdjustType('polyethene')}>Poly</Button></div>
              <div><label className="text-xs font-medium mb-1 block">Weight (g)</label><DecimalInput value={adjustAmount} onChange={v => setAdjustAmount(v)} placeholder="0.000" /></div>
              <div><label className="text-xs font-medium mb-1 block">Notes</label><Input value={adjustNotes} onChange={e => setAdjustNotes(e.target.value)} placeholder="Reason" /></div>
              <div className="flex gap-2"><Button className="flex-1" disabled={busy} onClick={() => handleAdjust(true)} data-testid="box-adjust-add-btn"><Plus size={14} className="mr-1" />{busy ? 'Saving…' : 'Add'}</Button><Button variant="destructive" className="flex-1" disabled={busy} onClick={() => handleAdjust(false)} data-testid="box-adjust-sub-btn"><Minus size={14} className="mr-1" />{busy ? 'Saving…' : 'Sub'}</Button></div>
            </CardContent></Card>
          </TabsContent>
        )}
      </Tabs>

      {/* Edit Dialog */}
      <Dialog open={showEdit} onOpenChange={setShowEdit}><DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto"><DialogHeader><DialogTitle>Edit Box: {box?.box_id}</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div><label className="text-sm font-medium mb-1 block">Type</label><Select value={editForm.box_type} onValueChange={v => setEditForm({...editForm, box_type: v})}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent><SelectItem value="regular">Regular</SelectItem><SelectItem value="brand">Brand Item</SelectItem></SelectContent></Select></div>
            <div><label className="text-sm font-medium mb-1 block flex items-center gap-1.5">Purity <Lock size={12} className="text-muted-foreground" /></label><div className="h-10 flex items-center px-3 rounded-md border bg-muted/60 cursor-not-allowed"><span className={`status-badge purity-${editForm.gold_purity} text-xs`}>{editForm.gold_purity}</span><span className="ml-2 text-xs text-muted-foreground">Fixed</span></div></div>
          </div>
          <div><label className="text-sm font-medium mb-1 block">Description</label><Textarea value={editForm.description} onChange={e => setEditForm({...editForm, description: e.target.value})} rows={2} className="text-sm" /></div>
          <div className="grid grid-cols-3 gap-3">
            <div><label className="text-sm font-medium mb-1 block">Gross (g)</label><DecimalInput value={editForm.gross_weight_g} onChange={v => setEditForm(f => ({...f, gross_weight_g: v}))} /></div>
            <div><label className="text-sm font-medium mb-1 block">Poly (g)</label><DecimalInput value={editForm.polyethene_weight_g} onChange={v => setEditForm(f => ({...f, polyethene_weight_g: v}))} /></div>
            <div><label className="text-sm font-medium mb-1 block">Box Wt (g)</label><DecimalInput value={editForm.box_weight_g} onChange={v => setEditForm(f => ({...f, box_weight_g: v}))} /></div>
          </div>
          <div className="p-2 rounded-lg border text-center" style={{ background: parseFloat(editAutoGold) < 0 ? 'hsl(0, 50%, 14%)' : 'hsl(43, 50%, 14%)', borderColor: parseFloat(editAutoGold) < 0 ? 'hsl(0, 50%, 25%)' : 'hsl(43, 50%, 25%)' }}><span className="text-xs" style={{ color: parseFloat(editAutoGold) < 0 ? 'hsl(0, 80%, 65%)' : 'hsl(43, 80%, 65%)' }}>Net Gold: </span><span className="font-mono font-bold" style={{ color: parseFloat(editAutoGold) < 0 ? 'hsl(0, 96%, 56%)' : 'hsl(43, 96%, 56%)' }}>{editAutoGold}g</span>{parseFloat(editAutoGold) < 0 && <span className="text-xs ml-2" style={{ color: 'hsl(0, 80%, 65%)' }}>(will be clamped to 0g)</span>}</div>
          <div>
            <label className="text-sm font-medium mb-2 block">Items</label>
            {allItems.length > 0 && <div className="max-h-32 overflow-y-auto space-y-1 p-2 rounded-lg border bg-muted/30">
              {[...allItems].sort((a, b) => { const sel = editForm.item_names || []; return (sel.includes(a.name) ? 0 : 1) - (sel.includes(b.name) ? 0 : 1); }).map(item => (<label key={item.id} className="flex items-center gap-2 cursor-pointer p-1 rounded hover:bg-muted/50 text-sm"><Checkbox checked={(editForm.item_names || []).includes(item.name)} onCheckedChange={() => { const list = editForm.item_names || []; setEditForm({...editForm, item_names: list.includes(item.name) ? list.filter(n => n !== item.name) : [...list, item.name]}); }} /><span>{item.name}</span></label>))}
            </div>}
          </div>
          <Button onClick={handleEditSave} disabled={busy} className="w-full" data-testid="box-detail-edit-save">{busy ? 'Saving…' : 'Save Changes'}</Button>
        </div>
      </DialogContent></Dialog>
    </div>
  );
}
